import React, { forwardRef } from "react";
import styled, { css } from "styled-components";
import { Input, InputRef } from "antd"; // Import InputRef from Ant Design
import { ColorPalette } from "./color/colorPalette";

const inputStyles = css<{ size: "large" | "xlarge" }>`
  border-radius: 4px;
  border-width: 0.7px;
  padding: 8px 12px;
  gap: 8px;
  height: ${({ size }) => (size === "large" ? "40px" : "48px")};

  &.ant-input {
    font-family: "YekanBakhVF" !important;
    font-size: 14px;
    font-weight: 325;
    line-height: 25.2px;
    text-align: right;
    border-color: ${ColorPalette.gray[500]};

    &:hover {
      border-color: ${ColorPalette.gray[900]};
    }
    &:focus {
      border-color: ${ColorPalette.primaryBlue[500]};
    }
    &.filled {
      background-color: ${ColorPalette.gray[50]};
      border-color: ${ColorPalette.gray[600]};
      color: ${ColorPalette.gray[950]};
    }
    &.disabled {
      border-color: ${ColorPalette.gray[200]};
      background-color: ${ColorPalette.gray[50]};
    }
    &.invalid {
      border-color: ${ColorPalette.red[300]};
      background-color: #ffffff; /* white */
    }
  }
`;

const StyledInput = styled(Input)<{ size: "large" | "xlarge" }>`
  ${inputStyles}
`;

interface InputWithIconsProps {
  size: "large" | "xlarge";
  className?: string; // Accept className
  LeftIconEnabled?: boolean;
  RightIconEnabled?: boolean;
  LeftIcon?: any;
  RightIcon?: any;
  disabled?: boolean;
  invalid?: boolean;
  type?: string;
  value?: string;
  placeholder?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onClickLeftIcon?: () => void;
  onClickRightIcon?: () => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  style?: React.CSSProperties;
  autoFocus?: boolean;
}

const InputWithIcons = forwardRef<InputRef, InputWithIconsProps>(
  (
    {
      size,
      className, // Destructure className
      LeftIconEnabled,
      RightIconEnabled,
      LeftIcon,
      RightIcon,
      disabled = false,
      invalid = false,
      type = "text",
      value,
      placeholder,
      onChange,
      onBlur,
      onClickLeftIcon,
      onClickRightIcon,
      onKeyPress,
      onKeyDown,
      style,
      autoFocus = false,
    },
    ref
  ) => {
    const inputClass = [
      value ? "filled" : "",
      disabled ? "disabled" : "",
      invalid ? "invalid" : "",
      className, // Append custom className
    ]
      .filter(Boolean)
      .join(" ");

    const handleIconClick = (
      event: React.MouseEvent,
      callback?: () => void
    ) => {
      event.stopPropagation();
      if (callback) callback();
    };

    return (
      <StyledInput
        ref={ref}
        type={type}
        size={size}
        className={inputClass}
        disabled={disabled}
        suffix={
          LeftIconEnabled ? (
            <LeftIcon
              className="icon left-icon"
              onClick={(e: any) => handleIconClick(e, onClickLeftIcon)}
            />
          ) : null
        }
        prefix={
          RightIconEnabled ? (
            <RightIcon
              className="icon right-icon"
              onClick={(e: any) => handleIconClick(e, onClickRightIcon)}
            />
          ) : null
        }
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
        onKeyDown={onKeyDown}
        style={style}
        autoFocus={autoFocus}
      />
    );
  }
);

export default InputWithIcons;
